import React, { useState } from "react";

import { setStateValue } from "@kazoohr/helpers";

import { useBlurHandlers } from "../../helpers/useBlurHandlers";
import { EmployeeList } from "../EmployeeList";
import { IconDictionary } from "../Icon/types";
import { Popover } from "../Popover";

import { FacepilePerson } from "./Facepile";
import styles from "./Facepile.scss";
import { FacepileFaces } from "./FacepileFaces";

export interface FacepileWithPopoverProps {
  circleClassNames: string[];
  dimensions?: number;
  max: number;
  overflowClassNames: string;
  size: "large" | "medium" | "small";
  tag?: string;
  users: FacepilePerson[];
  wrapperClass: string;
  showAll?: boolean;
  openProfileInNewTab?: boolean;
  disableProfileLink?: boolean;
  profilePicIconOverride?: IconDictionary;
  overridePeopleCount?: number;
  active?: boolean;
  highlightActiveFace?: boolean;
  activeUserId?: string;
  onUserClick?: (user: FacepilePerson) => void;
  onUserCountClick?: () => void;
}

export function FacepileWithPopover({
  circleClassNames,
  dimensions,
  max,
  overflowClassNames,
  size,
  tag,
  users,
  wrapperClass,
  showAll = false,
  openProfileInNewTab = false,
  disableProfileLink = false,
  profilePicIconOverride,
  overridePeopleCount,
  active = false,
  highlightActiveFace = false,
  activeUserId,
  onUserClick,
  onUserCountClick,
}: FacepileWithPopoverProps): React.ReactElement {
  const [focused, setFocused] = useState(false);
  const [blurHandler, childFocusHandler] = useBlurHandlers(
    setStateValue(setFocused, false)
  );
  const [mouseOver, setMouseOver] = useState(false);
  const focusHandler = getFocusHandler(setFocused, childFocusHandler);

  const showList = mouseOver || focused;
  const cardClass = showAll ? "employee-list-expanded" : "employee-list";

  return (
    <div
      data-testid="facepile-anchor"
      onMouseOver={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
      tabIndex={0}
      onFocus={focusHandler}
      onBlur={blurHandler}
    >
      {/* TODO: KAZ-1265 There will be an accessability issue with tabbing
       * because Popover components display at the root of the body
       * https://projectnewco.atlassian.net/browse/KAZ-1265
       */}
      <Popover
        parent={
          <FacepileFaces
            users={users}
            max={max}
            dimensions={dimensions}
            size={size}
            tag={tag}
            circleClassNames={circleClassNames}
            overflowClassNames={overflowClassNames}
            wrapperClass={wrapperClass}
            profilePicIconOverride={profilePicIconOverride}
            overridePeopleCount={overridePeopleCount}
            active={active}
            highlightActiveFace={highlightActiveFace}
            activeUserId={activeUserId}
            onUserClick={onUserClick}
            onUserCountClick={onUserCountClick}
          />
        }
        isOpen={showList}
        popoverComponent={
          <EmployeeList
            openProfileInNewTab={openProfileInNewTab}
            employees={users}
            heightLimited={true}
            showSecondaryText={showAll}
            showDivider={showAll}
            disableProfileLink={disableProfileLink}
            profilePicIconOverride={profilePicIconOverride}
          />
        }
        position="bottom-start"
        className={styles[cardClass]}
      />
    </div>
  );
}

export function getFocusHandler(
  setFocused: (value: boolean) => void,
  childFocusHandler: () => void
): () => void {
  return () => {
    setFocused(true);
    childFocusHandler();
  };
}
